<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/yup"
import * as yup from "yup"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import type { Building } from "@types"
import { useBuildingStore } from "@core/stores/building-v2.store"

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<{
    readonly: boolean
    /** Bâtiment en consultation seule : champs désactivés (pas `readonly`). */
    disabled?: boolean
}>()

const getFieldReadonly = computed(() => props.readonly && !props.disabled)
const getFieldDisabled = computed(() => Boolean(props.disabled))

// ================================
// COMPOSABLES
// ================================
const buildingStore = useBuildingStore()
const { building } = storeToRefs(buildingStore)

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Informations du bâtiment",
            sectionGeneral: "Informations générales",
            sectionMeasures: "Mesures",
            sectionMaterials: "Matériaux",
            sectionDpe: "DPE",
            sectionAdditional: "Informations supplémentaires détectées",
            year: "Année de construction",
            floors: "Nombre d’étages",
            lots: "Nombre de lots",
            surfHab: "Surface habitable",
            surfToit: "Surface de toiture",
            surfFacade: "Surface de façade",
            height: "Hauteur du bâtiment",
            walls: "Murs",
            roof: "Toit",
            raccordement: "Raccordement",
            surfaceParcelle: "Surface de la parcelle",
            numeroParcelle: "Numéro de la parcelle",
            noData: "-",
            save: "Sauvegarder",
            cancel: "Annuler",
            validation: {
                number: "Saisissez un nombre valide.",
                yearMin: "L'année doit être supérieure ou égale à 1700.",
                yearMax: "L'année doit être inférieure ou égale à 2100.",
                min0: "La valeur doit être positive ou nulle.",
            },
            hintYear:
                "Donnée récupérée depuis la base nationale des fichiers fonciers",
            hintFloors:
                "Donnée récupérée depuis la base nationale des fichiers fonciers",
            hintLots:
                "Donnée récupérée depuis le Registre national d'Immatriculation des Copropriétés",
            hintSurfHab:
                "Donnée récupérée depuis la base de données nationale des bâtiments",
            hintSurfToit:
                "Donnée calculée par Homeys grace à la base de données nationale des bâtiments",
            hintSurfFacade:
                "Donnée calculée par Homeys grâce à la base de données nationale des bâtiments",
            hintHeight:
                "Donnée récupérée depuis la base de données topographique",
            hintWalls:
                "Donnée récupérée depuis la base nationale des fichiers fonciers",
            hintRoof:
                "Donnée récupérée depuis la base nationale des fichiers fonciers",
            suffixSquareMeter: "m²",
            suffixMeter: "m",
        },
    },
})

const getValidationSchema = computed(() =>
    toTypedSchema(
        yup.object({
            anneeConstruction: yup
                .number()
                .typeError(t("validation.number"))
                .min(1700, t("validation.yearMin"))
                .max(2100, t("validation.yearMax"))
                .nullable(),
            etages: yup
                .number()
                .typeError(t("validation.number"))
                .min(0, t("validation.min0"))
                .nullable(),
            lots: yup
                .number()
                .typeError(t("validation.number"))
                .min(0, t("validation.min0"))
                .nullable(),
            surfHab: yup
                .number()
                .typeError(t("validation.number"))
                .min(0, t("validation.min0"))
                .nullable(),
            surfToit: yup
                .number()
                .typeError(t("validation.number"))
                .min(0, t("validation.min0"))
                .nullable(),
            surfFacade: yup
                .number()
                .typeError(t("validation.number"))
                .min(0, t("validation.min0"))
                .nullable(),
            hauteur: yup
                .number()
                .typeError(t("validation.number"))
                .min(0, t("validation.min0"))
                .nullable(),
            murs: yup.string().optional(),
            toit: yup.string().optional(),
        })
    )
)

/**
 * Relie l'état d'erreur vee-validate à la prop PrimeVue `invalid` (InputNumber, InputText, Select, etc.).
 */
function getPrimeInvalidFieldProps(state: { errors: string[] }) {
    return {
        props: {
            invalid: state.errors.length > 0,
        },
    }
}

const { handleSubmit, defineField, setFieldValue, errors } = useForm({
    validationSchema: getValidationSchema,
    initialValues: {
        anneeConstruction: null as number | null,
        etages: null as number | null,
        lots: null as number | null,
        surfHab: null as number | null,
        surfToit: null as number | null,
        surfFacade: null as number | null,
        hauteur: null as number | null,
        murs: "",
        toit: "",
    },
})

const [anneeConstruction, anneeConstructionAttrs] = defineField(
    "anneeConstruction",
    getPrimeInvalidFieldProps
)
const [etages, etagesAttrs] = defineField("etages", getPrimeInvalidFieldProps)
const [lots, lotsAttrs] = defineField("lots", getPrimeInvalidFieldProps)
const [surfHab, surfHabAttrs] = defineField(
    "surfHab",
    getPrimeInvalidFieldProps
)
const [surfToit, surfToitAttrs] = defineField(
    "surfToit",
    getPrimeInvalidFieldProps
)
const [surfFacade, surfFacadeAttrs] = defineField(
    "surfFacade",
    getPrimeInvalidFieldProps
)
const [hauteur, hauteurAttrs] = defineField(
    "hauteur",
    getPrimeInvalidFieldProps
)
const [murs, mursAttrs] = defineField("murs", getPrimeInvalidFieldProps)
const [toit, toitAttrs] = defineField("toit", getPrimeInvalidFieldProps)

const equipementRef = ref<InstanceType<
    typeof BuildingInfoGeneralEquipement
> | null>(null)

function syncFromBuilding(): void {
    const b = building.value
    const bat = b?.infos?.batiment
    setFieldValue("anneeConstruction", bat?.annee_construction ?? null)
    setFieldValue("etages", bat?.etages ?? null)
    setFieldValue("lots", bat?.lots ?? null)
    setFieldValue("surfHab", bat?.surface?.habitable ?? null)
    setFieldValue("surfToit", bat?.surface?.toiture ?? null)
    setFieldValue("surfFacade", bat?.surface?.facade ?? null)
    setFieldValue("hauteur", bat?.hauteur ?? null)
    setFieldValue("murs", bat?.materiaux_murs ?? "")
    setFieldValue("toit", bat?.materiaux_toit ?? "")
    dpe.value = {
        energie: {
            value: bat?.dpe?.energie?.value ?? null,
            date: bat?.dpe?.energie?.date ?? null,
        },
        ges: {
            value: bat?.dpe?.ges?.value ?? null,
            date: bat?.dpe?.ges?.date ?? null,
        },
    }
}

const loading = ref(false)
const errorMsg = ref("")
const dpe = ref<Building["infos"]["batiment"]["dpe"]>({
    energie: {
        value: null,
        date: null,
    },
    ges: {
        value: null,
        date: null,
    },
})
watch(building, syncFromBuilding, { immediate: true, deep: true })

type AdditionalInfoRow = {
    label: string
    value: string
}

function resolveBuildingMongoId(b: Building): string {
    return b._id ?? b._id_building
}

function formatRaccordement(value?: {
    gaz?: boolean
    reseau_de_chaleur?: boolean
}): string {
    if (!value) return t("noData")
    if (value.gaz && value.reseau_de_chaleur) {
        return "Raccordabilité au réseau de gaz et présence d’un réseau de chaleur à proximité"
    }
    if (value.gaz) return "Raccordabilité au réseau de gaz"
    if (value.reseau_de_chaleur) {
        return "Présence d’un réseau de chaleur à proximité"
    }
    return t("noData")
}

/**
 * Compare le DPE local au DPE en store (dates normalisees).
 */
function isDpeEqualToBuilding(
    local: Building["infos"]["batiment"]["dpe"],
    fromBuilding: Building["infos"]["batiment"]["dpe"] | undefined
): boolean {
    const normDate = (x: unknown): string | null => {
        if (x == null) return null
        if (x instanceof Date) return x.toISOString()
        return String(x)
    }
    const pack = (d: Building["infos"]["batiment"]["dpe"] | undefined) =>
        JSON.stringify({
            eV: d?.energie?.value ?? null,
            eD: normDate(d?.energie?.date),
            gV: d?.ges?.value ?? null,
            gD: normDate(d?.ges?.date),
        })
    return pack(local) === pack(fromBuilding)
}

const getHasChanged = computed(() => {
    const b = building.value
    if (!b) return false
    const bat = b.infos?.batiment
    const n = (v: number | null | undefined) => v ?? null
    if (
        n(anneeConstruction.value) !== n(bat?.annee_construction) ||
        n(etages.value) !== n(bat?.etages) ||
        n(lots.value) !== n(bat?.lots) ||
        n(surfHab.value) !== n(bat?.surface?.habitable) ||
        n(surfToit.value) !== n(bat?.surface?.toiture) ||
        n(surfFacade.value) !== n(bat?.surface?.facade) ||
        n(hauteur.value) !== n(bat?.hauteur) ||
        (murs.value ?? "") !== (bat?.materiaux_murs ?? "") ||
        (toit.value ?? "") !== (bat?.materiaux_toit ?? "")
    ) {
        return true
    }
    if (!isDpeEqualToBuilding(dpe.value, bat?.dpe)) {
        return true
    }
    return equipementRef.value?.hasEnergyUseChanged() ?? false
})
const getAdditionalInfoRows = computed<AdditionalInfoRow[]>(() => {
    const b = building.value
    const raccordement = b?.infos?.raccordement ?? b?.geometry?.raccordement
    const surfaceParcelle = b?.infos?.batiment?.surface?.parcelle
    const numeroParcelle =
        b?.infos?.cadastre?.numero_parcelle ??
        b?.geometry?.cadastre?.numero_parcelle

    return [
        {
            label: t("numeroParcelle"),
            value:
                numeroParcelle != null && String(numeroParcelle).trim() !== ""
                    ? String(numeroParcelle)
                    : t("noData"),
        },
        {
            label: t("surfaceParcelle"),
            value:
                surfaceParcelle != null && String(surfaceParcelle).trim() !== ""
                    ? `${String(surfaceParcelle).trim()} m²`
                    : t("noData"),
        },
        {
            label: t("raccordement"),
            value: formatRaccordement(raccordement),
        },
    ]
})

const onSubmit = handleSubmit(async (values) => {
    const b = building.value
    if (!b || props.readonly || props.disabled) return
    try {
        loading.value = true
        errorMsg.value = ""
        const id = resolveBuildingMongoId(b)
        await buildingStore.updateBuildingById(
            id,
            {
                "infos.batiment.annee_construction":
                    values.anneeConstruction ?? undefined,
                "infos.batiment.etages": values.etages ?? undefined,
                "infos.batiment.lots": values.lots ?? undefined,
                "infos.batiment.hauteur": values.hauteur ?? undefined,
                "infos.batiment.materiaux_murs": values.murs ?? undefined,
                "infos.batiment.materiaux_toit": values.toit ?? undefined,
                "infos.batiment.surface.habitable": values.surfHab ?? undefined,
                "infos.batiment.surface.toiture": values.surfToit ?? undefined,
                "infos.batiment.surface.facade": values.surfFacade ?? undefined,
                "infos.batiment.dpe": dpe.value,
                "infos.batiment.energyuse":
                    equipementRef.value?.getEnergyUsePayload() ?? [],
            } as Record<string, unknown>,
            { silent: true }
        )
    } catch (e) {
        errorMsg.value = (e as Error).message
    } finally {
        loading.value = false
    }
})

function onCancel(): void {
    syncFromBuilding()
    equipementRef.value?.syncFromStore()
}
</script>

<template>
    <BaseTitledCard class="w-full shadow-sm">
        <template #header>
            {{ t("title") }}
        </template>
        <template #content>
            <form
                class="space-y-8 flex flex-col gap-8"
                @submit.prevent="onSubmit"
            >
                <!-- ================================ -->
                <!-- INFORMATIONS GENERALES -->
                <!-- ================================ -->
                <fieldset
                    class="grid grid-cols-1 md:grid-cols-3 gap-4 border-0 p-0 m-0"
                >
                    <legend
                        class="md:col-span-3 text-base font-semibold text-surface-900 mb-4"
                    >
                        {{ t("sectionGeneral") }}
                    </legend>
                    <BaseInputWrapper
                        :label="t('year')"
                        input-id="building-info-general-building-year"
                        :hint="t('hintYear')"
                        :error="errors.anneeConstruction ?? ''"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="anneeConstruction"
                            v-bind="anneeConstructionAttrs"
                            input-id="building-info-general-building-year"
                            class="w-full"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('floors')"
                        input-id="building-info-general-building-floors"
                        :hint="t('hintFloors')"
                        :error="errors.etages ?? ''"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="etages"
                            v-bind="etagesAttrs"
                            input-id="building-info-general-building-floors"
                            class="w-full"
                            :min="0"
                            :show-buttons="!getFieldDisabled"
                            button-layout="horizontal"
                            decrement-button-icon="pi pi-minus"
                            increment-button-icon="pi pi-plus"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                            :style="{
                                '--p-inputgroup-addon-border-radius': '0',
                            }"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('lots')"
                        input-id="building-info-general-building-lots"
                        :hint="t('hintLots')"
                        :error="errors.lots ?? ''"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="lots"
                            v-bind="lotsAttrs"
                            input-id="building-info-general-building-lots"
                            class="w-full"
                            :min="0"
                            :show-buttons="!getFieldDisabled"
                            button-layout="horizontal"
                            decrement-button-icon="pi pi-minus"
                            increment-button-icon="pi pi-plus"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                            :style="{
                                '--p-inputgroup-addon-border-radius': '0',
                            }"
                        />
                    </BaseInputWrapper>
                </fieldset>

                <!-- ================================ -->
                <!-- MESURES -->
                <!-- ================================ -->
                <fieldset
                    class="grid grid-cols-1 md:grid-cols-3 gap-4 border-0 p-0 m-0"
                >
                    <legend
                        class="md:col-span-3 text-base font-semibold text-surface-900 mb-4"
                    >
                        {{ t("sectionMeasures") }}
                    </legend>
                    <BaseInputWrapper
                        :label="t('surfHab')"
                        input-id="building-info-general-building-surface-habitable"
                        :suffix="t('suffixSquareMeter')"
                        :hint="t('hintSurfHab')"
                        :error="errors.surfHab ?? ''"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="surfHab"
                            v-bind="surfHabAttrs"
                            input-id="building-info-general-building-surface-habitable"
                            class="w-full"
                            :min="0"
                            :max-fraction-digits="2"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('surfToit')"
                        input-id="building-info-general-building-surface-toiture"
                        :suffix="t('suffixSquareMeter')"
                        :hint="t('hintSurfToit')"
                        :error="errors.surfToit ?? ''"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="surfToit"
                            v-bind="surfToitAttrs"
                            input-id="building-info-general-building-surface-toiture"
                            class="w-full"
                            :min="0"
                            :max-fraction-digits="2"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('surfFacade')"
                        input-id="building-info-general-building-surface-facade"
                        :suffix="t('suffixSquareMeter')"
                        :hint="t('hintSurfFacade')"
                        :error="errors.surfFacade ?? ''"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="surfFacade"
                            v-bind="surfFacadeAttrs"
                            input-id="building-info-general-building-surface-facade"
                            class="w-full"
                            :min="0"
                            :max-fraction-digits="2"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('height')"
                        input-id="building-info-general-building-height"
                        :suffix="t('suffixMeter')"
                        :hint="t('hintHeight')"
                        :error="errors.hauteur ?? ''"
                        unfloat
                    >
                        <BaseInputNumber
                            v-model="hauteur"
                            v-bind="hauteurAttrs"
                            input-id="building-info-general-building-height"
                            class="w-full"
                            :min="0"
                            :max-fraction-digits="2"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                        />
                    </BaseInputWrapper>
                </fieldset>

                <!-- ================================ -->
                <!-- MATERIAUX -->
                <!-- ================================ -->
                <fieldset
                    class="grid grid-cols-1 md:grid-cols-2 gap-4 border-0 p-0 m-0"
                >
                    <legend
                        class="md:col-span-2 text-base font-semibold text-surface-900 mb-4"
                    >
                        {{ t("sectionMaterials") }}
                    </legend>
                    <BaseInputWrapper
                        :label="t('walls')"
                        input-id="building-info-general-building-walls"
                        :hint="t('hintWalls')"
                        :error="errors.murs ?? ''"
                        unfloat
                    >
                        <BaseInputText
                            v-model="murs"
                            v-bind="mursAttrs"
                            input-id="building-info-general-building-walls"
                            class="w-full"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                        />
                    </BaseInputWrapper>
                    <BaseInputWrapper
                        :label="t('roof')"
                        input-id="building-info-general-building-roof"
                        :hint="t('hintRoof')"
                        :error="errors.toit ?? ''"
                        unfloat
                    >
                        <BaseInputText
                            v-model="toit"
                            v-bind="toitAttrs"
                            input-id="building-info-general-building-roof"
                            class="w-full"
                            :readonly="getFieldReadonly"
                            :disabled="getFieldDisabled"
                        />
                    </BaseInputWrapper>
                </fieldset>

                <!-- ================================ -->
                <!-- DPE -->
                <!-- ================================ -->
                <fieldset class="border-0 p-0 m-0">
                    <legend
                        class="text-base font-semibold text-surface-900 mb-4"
                    >
                        {{ t("sectionDpe") }}
                    </legend>
                    <BuildingInfoGeneralDpe
                        v-model="dpe"
                        :readonly="readonly"
                        :disabled="disabled"
                        display-hint
                    />
                </fieldset>

                <!-- ================================ -->
                <!-- EQUIPEMENTS + TYPOLOGIE CHAUFFAGE / ECS -->
                <!-- ================================ -->
                <BuildingInfoGeneralEquipement
                    ref="equipementRef"
                    scope="batiment"
                    :readonly="readonly"
                    :disabled="disabled"
                />

                <!-- ================================ -->
                <!-- INFORMATIONS SUPPLEMENTAIRES -->
                <!-- ================================ -->
                <fieldset class="flex flex-col gap-4 border-0 p-0 m-0">
                    <legend
                        class="md:col-span-2 text-base font-semibold text-surface-900 mb-4"
                    >
                        {{ t("sectionAdditional") }}
                    </legend>
                    <div
                        class="divide-y divide-surface-200 rounded-xl border border-surface-200 overflow-hidden"
                    >
                        <div
                            v-for="row in getAdditionalInfoRows"
                            :key="row.label"
                            class="flex flex-col gap-1 bg-white px-4 py-3 md:flex-row md:items-center md:justify-between"
                        >
                            <p class="text-sm font-medium text-surface-900">
                                {{ row.label }}
                            </p>
                            <p class="text-sm text-surface-700">
                                {{ row.value }}
                            </p>
                        </div>
                    </div>
                </fieldset>
                <div
                    v-if="getHasChanged && !readonly && !disabled"
                    class="flex flex-wrap justify-center gap-3"
                >
                    <BaseButton
                        type="button"
                        severity="secondary"
                        @click="onCancel"
                        :label="t('cancel')"
                    >
                    </BaseButton>
                    <BaseButton
                        type="submit"
                        :loading="loading"
                        :label="t('save')"
                    >
                    </BaseButton>
                </div>
                <BaseMessage v-if="errorMsg" severity="error" :closable="false">
                    {{ errorMsg }}
                </BaseMessage>
            </form>
        </template>
    </BaseTitledCard>
</template>
